<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Plantillas de contrato</h1>
    </div>

    <wz-table-container>
      <wz-table-toolbar>
        <template #left>
          <div style="min-width: 260px;">
            <wz-select
              v-model="companyId"
              :items="companies"
              defaultOption="Seleccionar empresa"
              @change="load"
            />
          </div>
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" :disabled="!companyId" @click="load">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre"
            @input="search = $event"
            @search="search = $event"
          />
          <wz-button :disabled="!companyId" @click="goCreate">Nueva plantilla</wz-button>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Tipos aplicables</th>
            <th>Creada</th>
            <th>Estado</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!companyId">
            <td colspan="5" class="text-center text-muted py-4">Seleccione una empresa.</td>
          </tr>
          <tr v-else-if="filteredList.length === 0">
            <td colspan="5" class="text-center text-muted py-4">Sin plantillas para esta empresa.</td>
          </tr>
          <tr v-for="t in filteredList" :key="t.id">
            <td>{{ t.name }}</td>
            <td>
              <wz-badge v-for="lbl in t.contractTypeLabels" :key="lbl" variant="primary" pill class="mr-1 mb-1">
                {{ lbl }}
              </wz-badge>
            </td>
            <td>{{ formatDate(t.createdAt) }}</td>
            <td>
              <wz-badge :variant="t.isActive ? 'success' : 'secondary'" pill>
                {{ t.isActive ? 'Activa' : 'Inactiva' }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown :items="rowActions(t)" placement="left" @click="handleAction(t, $event)" />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ filteredList.length }} registro(s)</span>
      </wz-table-footer>
    </wz-table-container>
  </div>
</template>

<script>
import { Alert, Util } from '@wizepe/wizeui'
import fileDownload from 'js-file-download'
import { CompanyService } from '@/core/services'
import ContractTemplateService from '@/core/services/ContractTemplateService'

export default {
  name: 'ContractTemplateList',
  data() {
    return {
      companyId: null,
      companies: [],
      templates: [],
      search: '',
    }
  },
  computed: {
    filteredList() {
      if (!this.search) return this.templates
      const norm = s => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      const q = norm(this.search)
      return this.templates.filter(t => norm(t.name).includes(q))
    },
  },
  async created() {
    await this.loadCompanies()
    if (this.$route.query.companyId) {
      this.companyId = Number(this.$route.query.companyId)
      await this.load()
    }
  },
  methods: {
    formatDate(d) {
      if (!d) return '—'
      const iso = String(d).substring(0, 10)
      const parts = iso.split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : iso
    },
    rowActions(t) {
      const actions = [
        { key: 'edit', label: 'Editar' },
        { key: 'download', label: 'Descargar DOCX' },
      ]
      if (t.isActive) actions.push({ key: 'disable', label: 'Desactivar' })
      return actions
    },
    handleAction(t, item) {
      if (item.key === 'edit') this.goEdit(t)
      if (item.key === 'download') this.downloadOriginal(t)
      if (item.key === 'disable') this.confirmDisable(t)
    },
    goCreate() {
      this.$router.push({ name: 'contract-template-create', query: { companyId: this.companyId } })
    },
    goEdit(t) {
      this.$router.push({ name: 'contract-template-edit', params: { id: t.id }, query: { companyId: this.companyId } })
    },
    async loadCompanies() {
      try {
        const resp = await CompanyService.listAll()
        this.companies = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
      } catch (e) {
        Alert.error('Error al cargar empresas')
      }
    },
    async load() {
      if (!this.companyId) return
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await ContractTemplateService.listByCompany(this.companyId, false)
        this.templates = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar plantillas')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async confirmDisable(t) {
      // Antes de eliminar/desactivar, contar las variantes personalizadas de colaboradores (Decisión 3A):
      // si hay, se eliminarán en cascada, así que se advierte explícitamente.
      let count = 0
      try {
        const resp = await ContractTemplateService.variantsCount(t.id)
        count = resp.payload || 0
      } catch (e) { /* si el conteo falla, se usa el mensaje estándar */ }
      const message = count > 0
        ? `Esta plantilla tiene ${count} variante(s) personalizada(s) de colaboradores. Al eliminarla, también se eliminarán todas las variantes. ¿Continuar?`
        : `¿Desactivar la plantilla "${t.name}"?`
      Alert.dialog('Desactivar', message, async () => {
        try {
          await ContractTemplateService.remove(t.id)
          Alert.success('Plantilla desactivada')
          this.load()
        } catch (e) {
          Alert.error(e.errors ? e.errors.message : 'Error al desactivar')
        }
      })
    },
    async downloadOriginal(t) {
      try {
        const resp = await ContractTemplateService.downloadFile(t.id)
        fileDownload(resp.data, Util.getFileName(resp) || `${t.name}.docx`)
      } catch (e) {
        Alert.error('Error al descargar el DOCX')
      }
    },
  },
}
</script>

<style lang="stylus" scoped></style>
